import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Input, 
  Card, 
  Tag, 
  Typography,
  message,
  Popconfirm
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { legislativeNoticeService } from '../../services/legislativeNoticeService';
import { attachmentService } from '../../services/attachmentService';
import { useAuth } from '../../contexts/AuthContext';
import { LegislativeNotice, Attachment, LegislativeNoticeCategory } from '../../types';
import { formatLocalizedDateTime, formatSimpleDateTime } from '../../utils/dateUtils';

const { Title } = Typography;

// 테이블 스타일 정의
const tableStyles = `
  .legislative-notice-table .ant-table-tbody > tr > td:nth-child(3) {
    text-align: left !important;
  }
  
  .legislative-notice-table .ant-table-thead > tr > th:nth-child(3) {
    text-align: left !important;
  }
  
  .legislative-notice-table .ant-table-tbody > tr > td:nth-child(3) .ant-btn {
    text-align: left !important;
    justify-content: flex-start !important;
    display: flex !important;
  }
  
  .legislative-notice-table .ant-table-tbody > tr > td:nth-child(3) .ant-btn > span {
    text-align: left !important;
    width: 100% !important;
  }
`;

const LegislativeNoticeList: React.FC = () => {
  const [legislativeNotices, setLegislativeNotices] = useState<LegislativeNotice[]>([]);
  const [filteredLegislativeNotices, setFilteredLegislativeNotices] = useState<LegislativeNotice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const fetchLegislativeNotices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await legislativeNoticeService.getAllLegislativeNotices();
      setLegislativeNotices(data);
      setFilteredLegislativeNotices(data);
    } catch (error) {
      message.error(t('legislativeNotices.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // 검색 기능
  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredLegislativeNotices(legislativeNotices);
      return;
    }
    
    const filtered = legislativeNotices.filter(notice =>
      notice.title.toLowerCase().includes(value.toLowerCase()) ||
      notice.category.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredLegislativeNotices(filtered);
  };

  useEffect(() => {
    fetchLegislativeNotices();
  }, [fetchLegislativeNotices]);

  const handleDelete = async (legislativeNoticeId: string) => {
    try {
      await legislativeNoticeService.deleteLegislativeNotice(legislativeNoticeId);
      message.success(t('legislativeNotices.deleteSuccess'));
      fetchLegislativeNotices();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('legislativeNotices.deleteFailed'));
    }
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, decodeURIComponent(attachment.file_name));
    } catch (error: any) {
      message.error(t('legislativeNotices.downloadFailed'));
    }
  };

  // 마감일 상태 확인
  const getDeadlineStatus = (deadlineDate: string) => {
    // 서버 시간대(UTC) 기준으로 계산
    const deadline = new Date(deadlineDate + 'T00:00:00.000Z');
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    today.setHours(0, 0, 0, 0);
    
    const isExpired = today > deadline;
    
    if (isExpired) {
      return { color: 'red', text: t('legislativeNotices.deadlineStatus.expired'), icon: <ClockCircleOutlined /> };
    } else {
      const diffTime = deadline.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 3) {
        return { color: 'orange', text: t('legislativeNotices.deadlineStatus.urgent', { count: diffDays }), icon: <ClockCircleOutlined /> };
      } else {
        return { color: 'green', text: t('legislativeNotices.deadlineStatus.normal', { count: diffDays }), icon: <ClockCircleOutlined /> };
      }
    }
  };

  const getCategoryColor = (category: LegislativeNoticeCategory) => {
    switch (category) {
      case LegislativeNoticeCategory.KYOGYU:
        return 'blue';
      case LegislativeNoticeCategory.KYORYUNG:
        return 'purple';
      case LegislativeNoticeCategory.YEGYU:
        return 'cyan';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: t('legislativeNotices.table.number'),
      dataIndex: 'legislative_notice_id',
      key: 'legislative_notice_id',
      width: 50,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => filteredLegislativeNotices.length - index,
    },
    {
      title: t('legislativeNotices.table.category'),
      dataIndex: 'category',
      key: 'category',
      width: 70,
      align: 'center' as const,
      render: (category: LegislativeNoticeCategory) => {
        // 한글 카테고리 값을 영어 키로 매핑
        const getCategoryKey = (cat: string): string => {
          switch (cat) {
            case '교규': return 'KYOGYU';
            case '교령': return 'KYORYUNG';
            case '예규': return 'YEGYU';
            default: return cat;
          }
        };
        
        const categoryKey = getCategoryKey(category);
        return (
          <Tag color={getCategoryColor(category)} style={{ fontSize: '12px', padding: '2px 6px' }}>
            {t(`legislativeNotices.categories.${categoryKey}`)}
          </Tag>
        );
      },
    },
    {
      title: t('legislativeNotices.table.title'),
      dataIndex: 'title',
      key: 'title',
      width: isAdmin ? 200 : 280,
      ellipsis: false,
      align: 'left' as const,
      render: (text: string, record: LegislativeNotice) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/legislative-notices/${record.legislative_notice_id}`)}
          style={{ 
            padding: 0, 
            height: 'auto', 
            textAlign: 'left',
            width: '100%',
            overflow: 'visible',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            lineHeight: '1.3',
            fontSize: '14px',
            justifyContent: 'flex-start',
            display: 'flex'
          }}
          title={text}
        >
          {text}
        </Button>
      ),
    },
    {
      title: t('legislativeNotices.table.deadline'),
      dataIndex: 'deadline_date',
      key: 'deadline_date',
      width: 100,
      align: 'center' as const,
      render: (deadlineDate: string, record: LegislativeNotice) => {
        // 서버에서 계산된 마감일 상태 사용
        if (record.deadline_status) {
          let color = 'green';
          
          if (record.is_deadline_passed) {
            color = 'red';
          } else if (record.deadline_status.includes('3일') || record.deadline_status.includes('2일') || record.deadline_status.includes('1일')) {
            color = 'orange';
          }
          
          // 한글 상태 텍스트를 번역 키로 변환
          let displayText = record.deadline_status;
          if (record.deadline_status.includes('마감')) {
            displayText = t('legislativeNotices.deadlineStatus.expired');
          } else if (record.deadline_status.includes('일 남음')) {
            const daysMatch = record.deadline_status.match(/(\d+)일 남음/);
            if (daysMatch) {
              const days = parseInt(daysMatch[1], 10);
              displayText = t('legislativeNotices.deadlineStatus.urgent', { count: days });
            }
          }
          
          return (
            <Tag color={color} icon={<ClockCircleOutlined />} style={{ fontSize: '12px', padding: '2px 6px' }}>
              {displayText}
            </Tag>
          );
        }
        
        // 폴백: 클라이언트에서 계산
        const status = getDeadlineStatus(deadlineDate);
        return (
          <Tag color={status.color} icon={status.icon} style={{ fontSize: '12px', padding: '2px 6px' }}>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: t('legislativeNotices.table.attachments'),
      dataIndex: 'attachments',
      key: 'attachments',
      width: 60,
      align: 'center' as const,
      render: (attachments: Attachment[]) => {
        if (!attachments || attachments.length === 0) {
          return null;
        }
        
        return (
          <Space size="small">
            {attachments.slice(0, 3).map((attachment) => (
              <Button
                key={attachment.attachment_id}
                type="text"
                size="small"
                icon={<span style={{ fontSize: '16px' }}>📎</span>}
                onClick={() => handleDownloadAttachment(attachment)}
                style={{ 
                  padding: '1px 3px', 
                  minWidth: 'auto',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={decodeURIComponent(attachment.file_name)}
              />
            ))}
            {attachments.length > 3 && (
              <span style={{ fontSize: '12px', color: '#666' }}>
                +{attachments.length - 3}
              </span>
            )}
          </Space>
        );
      },
    },
    {
      title: (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2' }}>
          <span>{t('legislativeNotices.table.createdAt')}</span>
          <span style={{ fontSize: '11px', color: '#666' }}>({t('legislativeNotices.table.updatedAt')})</span>
        </div>
      ),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (date: string, record: LegislativeNotice) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
          <span title={formatLocalizedDateTime(date, i18n.language)} style={{ fontSize: '12px' }}>
            {formatSimpleDateTime(date)}
          </span>
          {record.updated_at && record.updated_at !== record.created_at && (
            <span title={formatLocalizedDateTime(record.updated_at, i18n.language)} style={{ fontSize: '11px', color: '#666' }}>
              ({formatSimpleDateTime(record.updated_at)})
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('legislativeNotices.table.viewCount'),
      dataIndex: 'view_count',
      key: 'view_count',
      width: 60,
      align: 'center' as const,
      render: (count: number) => count.toLocaleString(),
    },
    {
      title: t('legislativeNotices.table.comments'),
      dataIndex: 'comment_count',
      key: 'comment_count',
      width: 60,
      align: 'center' as const,
      render: (count: number = 0) => (
        <Tag color="blue" icon={<MessageOutlined />} style={{ fontSize: '12px', padding: '2px 6px' }}>
          {count}
        </Tag>
      ),
    },
    // 관리자만 볼 수 있는 승인 상태 컬럼
    ...(isAdmin ? [
      {
        title: t('legislativeNotices.table.approvalStatus'),
        dataIndex: 'is_approved',
        key: 'is_approved',
        width: 80,
        align: 'center' as const,
        render: (isApproved: boolean) => (
          <Tag color={isApproved ? 'green' : 'orange'}>
            {isApproved ? t('legislativeNotices.table.approved') : t('legislativeNotices.table.pending')}
          </Tag>
        ),
      },
    ] : []),
    // 관리자만 볼 수 있는 컬럼들
    ...(isAdmin ? [
      {
        title: t('legislativeNotices.table.actions'),
        key: 'actions',
        width: 80,
        align: 'center' as const,
        render: (_: any, record: LegislativeNotice) => (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/legislative-notices/${record.legislative_notice_id}`)}
              style={{ padding: '1px 3px', minWidth: 'auto' }}
            />
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/legislative-notices/${record.legislative_notice_id}/edit`)}
              disabled={record.is_approved}
              style={{ 
                padding: '1px 3px', 
                minWidth: 'auto',
                opacity: record.is_approved ? 0.5 : 1
              }}
              title={record.is_approved ? t('legislativeNotices.approval.cannotEdit') : t('legislativeNotices.table.edit')}
            />

            <Popconfirm
              title={record.is_approved ? t('legislativeNotices.approval.cannotDelete') : t('legislativeNotices.approval.deleteConfirm')}
              onConfirm={() => !record.is_approved && handleDelete(record.legislative_notice_id)}
              okText={t('legislativeNotices.table.delete')}
              cancelText={t('common.cancel')}
              disabled={record.is_approved}
            >
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                disabled={record.is_approved}
                style={{ 
                  padding: '1px 3px', 
                  minWidth: 'auto',
                  opacity: record.is_approved ? 0.5 : 1
                }}
                title={record.is_approved ? t('legislativeNotices.approval.cannotDelete') : t('legislativeNotices.table.delete')}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ] : []),
  ];

  return (
    <div style={{ padding: '16px' }}>
      <style>{tableStyles}</style>
      <Card style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            {t('legislativeNotices.title')}
          </Title>
          <Space>
            <Input.Search
              placeholder={t('legislativeNotices.searchPlaceholder')}
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              value={searchText}
            />
            {isAdmin && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/legislative-notices/create')}
              >
                {t('legislativeNotices.create')}
              </Button>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredLegislativeNotices}
          rowKey="legislative_notice_id"
          loading={loading}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('pagination.showTotal', { 
              start: range[0], 
              end: range[1], 
              total: total 
            }),
          }}
          scroll={{ x: 800 }}
          size="small"
          style={{
            fontSize: '14px'
          }}
          className="legislative-notice-table"
        />
      </Card>
    </div>
  );
};

export default LegislativeNoticeList; 